import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getData, insertData, isQuizEnabled, resolveMediaUrl, fetchActiveCards, computeCardStatus, isCardsEnabled, isSchemasEnabled } from "../api";
import type { CardStatus } from "../api";
import EvaluationChart from "../components/EvaluationChart";
import { Box,Button, Typography, List, Avatar, Modal, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import CommentsList from "../components/CommentsList";
import EvaluationItem from "../components/EvaluationItem";
import { useNavigate } from "react-router-dom"; // Import de `useNavigate`
import {
  SPECIAL_CATEGORIES,
  loadSpecialAssignments,
  getExercisesForSpecialCategory
} from "../utils/specialCategories";

import theme from "../styles/theme";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface Video {
  id: number;
  title: string;
  url: string;
  description?: string;
  thumbnail?: string;
}

interface Exercise {
  id: number;
  title: string;
  description: string;
  max_points: number;
  repetitions: number;
  recommended_videos: string;
  category_id: number;
}

interface Category {
  id: number;
  name: string;
}


export default function PlayerProfile() {
  const { id } = useParams();
  // Cartons actifs de la joueuse (bandeau discipline en haut de page).
  const [cardStatus, setCardStatus] = useState<CardStatus | null>(null);
  useEffect(() => {
    if (!id) return;
    void isCardsEnabled().then((enabled) => {
      if (!enabled) {
        setCardStatus(null);
        return;
      }
      return fetchActiveCards(id).then((rows) => setCardStatus(computeCardStatus(rows)));
    });
  }, [id]);
  const [evaluations, setEvaluations] = useState<any[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [selectedSession, setSelectedSession] = useState<number | null>(null);
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [exerciseTitles, setExerciseTitles] = useState<{ [key: string]: string | number }>({});
  const [exerciseVideosIds, setExerciseVideosIds] = useState<{ [key: string]: string[] }>({});
  const [quizEnabled, setQuizEnabled] = useState(true);
  const [schemasEnabled, setSchemasEnabled] = useState(true);

  useEffect(() => {
    let cancelled = false;
    isQuizEnabled()
      .then((enabled) => { if (!cancelled) setQuizEnabled(enabled); })
      .catch(() => { if (!cancelled) setQuizEnabled(true); });
    isSchemasEnabled()
      .then((enabled) => { if (!cancelled) setSchemasEnabled(enabled); })
      .catch(() => { if (!cancelled) setSchemasEnabled(true); });
    return () => { cancelled = true; };
  }, []);
  const [videosById, setVideosById] = useState<{ [key: string]: Video }>({});
  const [profileData, setProfileData] = useState<any>(null);
  const [comments, setComments] = useState<any[]>([]);
  const navigate = useNavigate(); // Initialisation de `useNavigate`
  const [specialAssignments, setSpecialAssignments] = useState<any>({});


  // Ajout d'un commentaire (API + local)
  const handleAddComment = async (comment: { staff_id: string; visible_to_player: number; comment: string }) => {
    // Construction du payload pour la table "comments"
    const payload = {
      player_id: id,
      staff_id: comment.staff_id,
      visible_to_player: comment.visible_to_player,
      comment: comment.comment,
    };
    try {
      // Ajout API via insertData
      const res = await insertData("comments", payload);
      setComments((prev) => [
        {
          ...payload,
          id: res?.data?.[0]?.id || Math.random().toString(36).slice(2),
          date_comment: new Date().toISOString(),
        },
        ...prev,
      ]);
    } catch (e) {
      setComments((prev) => [
        {
          ...payload,
          id: Math.random().toString(36).slice(2),
          date_comment: new Date().toISOString(),
        },
        ...prev,
      ]);
    }
  };

  useEffect(() => {
    setSpecialAssignments(loadSpecialAssignments());
  }, []);
  

  // Charger la liste des sessions au montage, puis sélectionner la dernière par défaut
  useEffect(() => {
    getData("sessions", { order_by: "start_date DESC" }).then((res: any) => {
      setSessions(res.data || []);
      if (res.data && res.data.length > 0) {
        setSelectedSession(res.data[0].id);
      }
    });
  }, [id]);

  // Charger les données selon la session sélectionnée
  useEffect(() => {
    if (!selectedSession) return;
    // Charger les évaluations du joueur pour la session sélectionnée
    getData("evaluations", { where: `player_id = ${id} AND session_id = ${selectedSession}`, order_by: "session_id DESC" })
      .then((res: any) => {
        setEvaluations(res.data || []);
      });

    // Charger les catégories d’exercices
    getData("exercise_categories").then((res: any) => {
      setCategories(res.data || []);
    });

    // Charger les exercices
    getData("exercises").then(async (res: any) => {
      const exos: Exercise[] = res.data || [];
      setExercises(exos);

      // Construire les maps pour titres, vidéos, etc.
      const titles: { [key: string]: string | number } = {};
      const vids: { [key: string]: string[] } = {};
      const allVideoIds: Set<string> = new Set();

      exos.forEach((exercise) => {
        const idStr = exercise.id.toString();
        titles[idStr] = exercise.title;
        titles[`${idStr}_description`] = exercise.description;
        titles[`${idStr}_max_points`] = exercise.max_points;
        titles[`${idStr}_repetitions`] = exercise.repetitions;

        try {
          const parsed = JSON.parse(exercise.recommended_videos);
          if (Array.isArray(parsed)) {
            const ids = parsed.map((v: any) => v.toString());
            vids[idStr] = ids;
            ids.forEach((vid) => allVideoIds.add(vid));
          } else {
            vids[idStr] = [];
          }
        } catch {
          vids[idStr] = [];
        }
      });

      setExerciseTitles(titles);
      setExerciseVideosIds(vids);

      if (allVideoIds.size > 0) {
        const idList = Array.from(allVideoIds).join(",");
        const videosRes = await getData("videos", { where: `id IN (${idList})` });
        const videoMap: { [key: string]: Video } = {};
        (videosRes.data || []).forEach((video: Video) => {
          videoMap[video.id.toString()] = video;
        });
        setVideosById(videoMap);
      }
    });

    // Charger le profil
    getData("profiles", { where: `user_id = ${id}` }).then((res: any) => {
      setProfileData(res.data || null);
    });

    // Charger les commentaires
    getData("comments", { where: `player_id = ${id}` }).then((res: any) => {
      setComments(res.data || []);
    });
  }, [id, selectedSession]);

  const computeSpecialRate = (specialId: string) => {
    const exerciseIds = getExercisesForSpecialCategory(specialAssignments, specialId);
  
    const evals = evaluations.filter(ev =>
      exerciseIds.includes(ev.exercise_id)
    );
  
    if (evals.length === 0) return 0;
  
    let total = 0;
    evals.forEach(ev => {
      const scores: number[] = JSON.parse(ev.details || "[]");
      const totalScore = scores.reduce((a, b) => a + b, 0);
      const keyBase = ev.exercise_id.toString();
  
      const maxPoints = Number(exerciseTitles[`${keyBase}_max_points`] || 0);
      const repetitions = Number(exerciseTitles[`${keyBase}_repetitions`] || 0);
      const maxPossible = maxPoints * repetitions;
  
      if (maxPossible > 0) {
        total += (totalScore / maxPossible) * 100;
      }
    });
  
    return parseFloat((total / evals.length).toFixed(2));
  };
  

  // Fonction d’analyse d’une évaluation (inchangée)
  const calculateAnalysis = (evaluation: any) => {
    const scores: number[] = JSON.parse(evaluation.details || "[]");
    const totalScore = scores.reduce((a, b) => a + b, 0);
    const keyBase = evaluation.exercise_id.toString();
    const maxPoints = Number(exerciseTitles[`${keyBase}_max_points`] as unknown) || 0;
    const repetitions = Number(exerciseTitles[`${keyBase}_repetitions`] as unknown) || 0;
    const maxPossibleScore = maxPoints * repetitions;

    const successRate =
      maxPossibleScore > 0
        ? parseFloat(((totalScore / maxPossibleScore) * 100).toFixed(2))
        : 0;

    const mean = totalScore / (scores.length || 1);
    const variance =
      scores.reduce((sum, score) => sum + Math.pow(score - mean, 2), 0) /
      (scores.length || 1);
    const regularity = variance < 5 ? "Régulier" : "Irrégulier";

    return { totalScore, successRate, regularity };
  };

  // Construire un mapping exercice_id → catégorie
  const exerciseIdToCategory: { [key: string]: number } = {};
  exercises.forEach((ex) => {
    exerciseIdToCategory[ex.id.toString()] = ex.category_id;
  });

  // Mapping catégorie_id → nom
  const categoryIdToName: { [key: number]: string } = {};
  categories.forEach((cat) => {
    categoryIdToName[cat.id] = cat.name;
  });

  // Préparer les données cumulées par catégorie pour le graphique principal
  // On va agréger les taux de réussite (successRate) des évaluations par catégorie
  const categoryAggregates: {
    [catId: number]: { sumRate: number; count: number };
  } = {};

  evaluations.forEach((evalItem) => {
    const { successRate } = calculateAnalysis(evalItem);
    const cid = exerciseIdToCategory[evalItem.exercise_id.toString()];
    if (cid != null) {
      if (!categoryAggregates[cid]) {
        categoryAggregates[cid] = { sumRate: 0, count: 0 };
      }
      // on suppose successRate est un nombre
      categoryAggregates[cid].sumRate += successRate;
      categoryAggregates[cid].count += 1;
    }
  });

  // Suppression des lignes inutilisées

  // Regrouper les évaluations par catégorie pour affichage des items
  const evaluationsByCategory: {
    [cid: number]: any[];
  } = {};
  evaluations.forEach((evalItem) => {
    const cid = exerciseIdToCategory[evalItem.exercise_id.toString()];
    if (cid != null) {
      if (!evaluationsByCategory[cid]) {
        evaluationsByCategory[cid] = [];
      }
      evaluationsByCategory[cid].push(evalItem);
    } else {
      // si pas de catégorie identifiée, tu peux les regrouper sous “0” ou “Autres”
      if (!evaluationsByCategory[0]) {
        evaluationsByCategory[0] = [];
      }
      evaluationsByCategory[0].push(evalItem);
    }
  });

  // Choisir le joueur (premier élément si tableau)
  const player = Array.isArray(profileData) ? profileData[0] : profileData;
  //console.log("Player Profile Data:", player);

  // Modal pour zoom
  const [openModal, setOpenModal] = useState(false);

  const specialRadarData = SPECIAL_CATEGORIES.map(s => ({
    subject: s.name,
    score: computeSpecialRate(s.id),
    fullMark: 100,
    isSpecial: true
  }));
  

  return (
    <Box
      sx={{
        padding: { xs: 2, sm: 4 },
        background: theme.palette.background.default,
        minHeight: "100vh",
        fontFamily: theme.typography.fontFamily,
        maxWidth: "1200px",
        margin: "0 auto",
      }}
    >

      {/* Discipline : carton "arbitre" affiché bien centré, juste au-dessus des deux boutons */}
      {cardStatus && cardStatus.effective !== "none" && (() => {
        const isRed = cardStatus.effective === "red";
        const fromTwoYellows = isRed && cardStatus.reds === 0;
        const refCard = (color: "yellow" | "red", extra: object) => (
          <Box
            sx={{
              position: "absolute",
              width: 46,
              height: 66,
              borderRadius: "7px",
              border: "1px solid rgba(0,0,0,.18)",
              background:
                color === "red"
                  ? "linear-gradient(145deg, #f87171 0%, #ef4444 40%, #b91c1c 100%)"
                  : "linear-gradient(145deg, #fef08a 0%, #fde047 35%, #eab308 100%)",
              boxShadow: "0 5px 12px rgba(0,0,0,.3)",
              ...extra,
            }}
          />
        );
        return (
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2.5,
                px: { xs: 2, sm: 3 },
                py: 1.75,
                borderRadius: 3,
                border: "2px solid",
                borderColor: isRed ? theme.palette.error.main : theme.palette.warning.main,
                bgcolor: isRed ? "#fef2f2" : "#fffbeb",
                boxShadow: theme.shadows[2],
                maxWidth: 480,
              }}
            >
              {/* Le(s) carton(s) : deux jaunes en éventail derrière le rouge le cas échéant */}
              <Box sx={{ position: "relative", width: 64, height: 76, flexShrink: 0 }}>
                {fromTwoYellows && refCard("yellow", { left: 0, top: 6, transform: "rotate(-16deg)" })}
                {fromTwoYellows && refCard("yellow", { left: 6, top: 3, transform: "rotate(-6deg)" })}
                {refCard(isRed ? "red" : "yellow", { left: fromTwoYellows ? 14 : 9, top: 2, transform: "rotate(7deg)" })}
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: 17,
                    lineHeight: 1.3,
                    color: isRed ? theme.palette.error.main : "#b45309",
                  }}
                >
                  {isRed ? `Carton rouge${fromTwoYellows ? " (2 jaunes)" : ""}` : "Carton jaune"}
                </Typography>
                <Typography sx={{ fontSize: 13.5, color: theme.palette.text.secondary, mt: 0.25 }}>
                  {isRed
                    ? "Tu ne participes pas au prochain entraînement. Vois avec ton coach."
                    : "Avertissement : un 2e carton jaune devient un rouge."}
                </Typography>
                {cardStatus.cards
                  .filter((c) => c.reason)
                  .map((c) => (
                    <Typography key={c.id} sx={{ fontSize: 12.5, color: theme.palette.text.secondary, mt: 0.25 }}>
                      • {c.reason}
                    </Typography>
                  ))}
              </Box>
            </Box>
          </Box>
        );
      })()}

      {/* Accès rapide aux schémas */}
      <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mb: 3, flexWrap: "wrap" }}>
        {schemasEnabled && (
          <Button variant="contained" color="primary" onClick={() => navigate("/schema-studio")}>
            📋 Réviser schéma
          </Button>
        )}
        {quizEnabled && (
          <Button variant="contained" color="secondary" onClick={() => navigate("/schema-quiz")}>
            🧩 Quiz schéma
          </Button>
        )}
      </Box>

      {/* Sélecteur de session aligné à droite */}
      <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 3 }}>
        {["coach", "staff"].includes(JSON.parse(localStorage.getItem("user") || "{}").role) && (
        <Button
          variant="outlined"
          color="primary"
          onClick={() => navigate("/dashboard")}
          sx={{ marginBottom: 4 }}
        >
          ← Retour au Tableau de bord
        </Button>
      )}
        <FormControl sx={{ minWidth: 220 }}>
          <InputLabel id="session-select-label">Session</InputLabel>
          <Select
            labelId="session-select-label"
            value={selectedSession || ""}
            label="Session"
            onChange={(e) => setSelectedSession(Number(e.target.value))}
          >
            {sessions.map((session) => (
              <MenuItem key={session.id} value={session.id}>
                {session.name} ({session.start_date})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {player && (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "center", md: "stretch" },
            marginBottom: 4,
            gap: 4,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 260,
              flex: { xs: "1 1 100%", md: "0 0 50%" },
              p: 3,
              marginBottom: { xs: 2, md: 0 },
            }}
          >
            <Avatar
              src={resolveMediaUrl(player.photo)}
              alt={
                !player.photo && player.nom_complet
                  ? player.nom_complet
                      .split(" ")
                      .map((n: string) => n[0])
                      .join("")
                      .toUpperCase()
                  : null
              }
              sx={{
                width: 120,
                height: 120,
                mb: 2,
                border: `3px solid ${theme.palette.primary.main}`,
              }}
            />
            <Typography
              variant="h1"
              sx={{
                fontSize: theme.typography.h1.fontSize,
                fontWeight: theme.typography.h1.fontWeight,
                color: theme.palette.primary.main,
                mb: 1,
                textAlign: "center",
                fontFamily: theme.typography.fontFamily,
              }}
            >
              {player.nom_complet || "Profil sans nom"}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                textAlign: "center",
                color: theme.palette.text.secondary,
                fontSize: "1.1rem",
                fontFamily: theme.typography.fontFamily,
              }}
            >
              {player.annee
                ? new Date().getFullYear() - Number(player.annee)
                : "N/A"}{" "}
              ans ({player.annee})
            </Typography>
            <Typography
              variant="body1"
              sx={{
                textAlign: "center",
                color: theme.palette.text.secondary,
                fontSize: "1.1rem",
                fontFamily: theme.typography.fontFamily,
              }}
            >
              {player.bio}
            </Typography>
            {Number(player?.note_assiduite || 0) > 0 && (
              <Typography
                variant="body1"
                sx={{
                  textAlign: "center",
                  color: theme.palette.text.secondary,
                  fontSize: "1.1rem",
                  fontFamily: theme.typography.fontFamily,
                }}
              >
                Assiduité : {" "}
                {Array.from({ length: 10 }, (_, i) => (
                  <span key={i} style={{ color: i < Number(player.note_assiduite) ? "#FFD700" : "#E0E0E0" }}>
                    ★
                  </span>
                ))}
              </Typography>
            )}
            {Number(player?.note_motivation || 0) > 0 && (
              <Typography
                variant="body1"
                sx={{
                  textAlign: "center",
                  color: theme.palette.text.secondary,
                  fontSize: "1.1rem",
                  fontFamily: theme.typography.fontFamily,
                }}
              >
                Motivation : {" "}
                {Array.from({ length: 10 }, (_, i) => (
                  <span key={i} style={{ color: i < Number(player.note_motivation) ? "#FFD700" : "#E0E0E0" }}>
                    ★
                  </span>
                ))}
              </Typography>
            )}
            {Number(player?.note_comportement || 0) > 0 && (
              <Typography
                variant="body1"
                sx={{
                  textAlign: "center",
                  color: theme.palette.text.secondary,
                  fontSize: "1.1rem",
                  fontFamily: theme.typography.fontFamily,
                }}
              >
                Comportement : {" "}
                {Array.from({ length: 10 }, (_, i) => (
                  <span key={i} style={{ color: i < Number(player.note_comportement) ? "#FFD700" : "#E0E0E0" }}>
                    ★
                  </span>
                ))}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: theme.palette.background.paper,
              borderRadius: theme.shape.borderRadius,
              boxShadow: "0 2px 12px rgba(102,41,192,0.04)",
              p: 3,
              position: "relative",
              width: "100%", // S'assure que le graphique prend toute la largeur
              minHeight: "450px",
              maxWidth: { xs: "100%", md: "600px" }, // Limite la largeur sur les écrans plus grands
              margin: "0 auto", // Centre le graphique
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: { xs: "300px", sm: "400px" }, // Ajuste la hauteur pour les petits écrans
                zIndex: 1,
              }}
            >
              {/* Graphique global par catégorie */}
                <EvaluationChart
                  evaluations={evaluations}
                  exerciseTitles={exerciseTitles}
                  exerciseIdToCategory={exerciseIdToCategory}
                  categoryIdToName={categoryIdToName}
                  specialRadarData={specialRadarData}
                />
            </Box>
            <Modal
              open={openModal}
              onClose={() => setOpenModal(false)}
              aria-labelledby="modal-graphique-global"
              sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <Box
                sx={{
                  width: "90vw",
                  height: "90vh",
                  display: "flex",
                  flexDirection: "column",
                  background: theme.palette.background.paper,
                  borderRadius: theme.shape.borderRadius,
                  boxShadow: "0 2px 24px rgba(102,41,192,0.12)",
                  p: 4,
                  position: "relative",
                }}
              >
                <button
                  onClick={() => setOpenModal(false)}
                  style={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    background: theme.palette.error.main,
                    color: theme.palette.background.paper,
                    border: "none",
                    borderRadius: theme.shape.borderRadius,
                    padding: "8px 16px",
                    fontWeight: "bold",
                    fontFamily: theme.typography.fontFamily,
                    cursor: "pointer",
                    boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
                  }}
                  aria-label={"Fermer le graphique"}
                >
                  Fermer
                </button>
                <Box sx={{ flex: 1, width: "100%" }}>
                  <EvaluationChart
                      evaluations={evaluations}
                      exerciseTitles={exerciseTitles}
                      exerciseIdToCategory={exerciseIdToCategory}
                      categoryIdToName={categoryIdToName}
                      specialRadarData={specialRadarData}
                    />
                </Box>
              </Box>
            </Modal>
          </Box>
        </Box>
      )}

      <Box sx={{ marginTop: 4 }}>
        <Typography
          variant="h2"
          sx={{
            marginBottom: 2,
            marginTop: { xs: 34, sm: 15 },
            color: theme.palette.secondary.main,
            fontFamily: theme.typography.fontFamily,
          }}
        >
          Analyse par catégorie
        </Typography>
        <List sx={{ width: "100%" }}>
          {/** Pour chaque catégorie, on affiche son nom + ses évaluations */}
          {Object.entries(evaluationsByCategory).map(([cidStr, evals]) => {
            const cid = parseInt(cidStr, 10);
            const catName = categoryIdToName[cid] || "Autres";

            return (
              <Box key={cid} sx={{ marginBottom: 4 }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: theme.typography.h3.fontSize,
                    fontWeight: theme.typography.h3.fontWeight,
                    color: theme.palette.primary.main,
                    mb: 2,
                    fontFamily: theme.typography.fontFamily,
                  }}
                >
                  {catName}
                </Typography>
                {evals.map((evaluation) => {
                  const { totalScore, successRate, regularity } = calculateAnalysis(evaluation);
                  const keyBase = evaluation.exercise_id.toString();
                  const exerciseTitle = String(exerciseTitles[keyBase] || "Exercice inconnu");
                  const videoIds = exerciseVideosIds[keyBase] || [];
                  const resolvedVideos = videoIds
                    .map((vid) => videosById[vid])
                    .filter(Boolean);

                  const scores: number[] = JSON.parse(evaluation.details || "[]");
                  const averageScore =
                    scores.length > 0
                      ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(2)
                      : "N/A";
                  const maxPoints = Number(exerciseTitles[`${keyBase}_max_points`] as unknown) || 0;

                  const barData = {
                    labels: scores.map((_, idx) => `Tentative ${idx + 1}`),
                    datasets: [
                      {
                        label: "Score",
                        data: scores,
                        backgroundColor: "rgba(75, 192, 192, 0.6)",
                      },
                    ],
                  };

                  const barOptions = {
                    responsive: true,
                    plugins: {
                      legend: { position: "top" as const },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        max: maxPoints,
                        title: {
                          display: true,
                          text: `Score (sur ${maxPoints})`,
                        },
                      },
                    },
                  };

                  return (
                    <EvaluationItem
                      key={evaluation.id}
                      evaluation={evaluation}
                      exerciseTitle={exerciseTitle}
                      totalScore={totalScore}
                      maxPoints={maxPoints}
                      scores={scores}
                      averageScore={averageScore}
                      successRate={successRate}
                      regularity={regularity}
                      resolvedVideos={resolvedVideos}
                      barData={barData}
                      barOptions={barOptions}
                    />
                  );
                })}
              </Box>
            );
          })}
        </List>
      </Box>

      <Box sx={{ marginTop: 4 }}>
        <Typography
          variant="h2"
          sx={{
            marginBottom: 2,
            color: theme.palette.secondary.main,
            fontFamily: theme.typography.fontFamily,
          }}
        >
          Commentaires
        </Typography>
  <CommentsList comments={comments} onAddComment={handleAddComment} />
      </Box>

      {/* Media queries CSS */}
      <style>{`
        @media (max-width: 900px) {
          .MuiBox-root {
            padding: 8px !important;
          }
          .MuiTypography-h1 {
            font-size: 1.5rem !important;
          }
          .MuiTypography-h2 {
            font-size: 1.2rem !important;
          }
        }
        @media (max-width: 600px) {
          .MuiBox-root {
            padding: 4px !important;
          }
          .MuiTypography-h1 {
            font-size: 1.1rem !important;
          }
          .MuiTypography-h2 {
            font-size: 1rem !important;
          }
        }
      `}</style>
    </Box>
  );
}
